/**
 * Utilidades de procesamiento de imágenes para Bizaclia.
 * Este módulo es client-safe (usa Canvas API del navegador).
 *
 * Para importaciones masivas desde API/CSV sin navegador:
 *   - Subir el archivo original al bucket como fallback.
 *   - El registro en `media` queda con `watermark_path: null`.
 *   - La UI cae automáticamente al original si no existe watermark.
 *   - En el futuro se puede generar el watermark en lote con un
 *     servicio externo (Cloudflare Images, Sharp-as-a-Service, etc.)
 */

import bizacliaLogoWhite from "@/assets/bizaclia-logo-white.png.asset.json";

export interface ProcessedImage {
  original: Blob;
  watermarked: Blob;
  width: number;
  height: number;
  sizeBytes: number;
  mimeType: string;
}

const MAX_DIMENSION = 1600;
const IMAGE_QUALITY = 0.72;
const OUTPUT_FORMAT = "image/webp";
const WATERMARK_LOGO_URL = bizacliaLogoWhite.url;

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

function canvasToBlob(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error("Canvas toBlob devolvió null"));
      },
      type,
      quality,
    );
  });
}

/**
 * Dibuja una marca de agua tipo texto sobre el canvas ya renderizado.
 * Posición: esquina inferior derecha, blanco semitransparente.
 */
let watermarkImagePromise: Promise<HTMLImageElement> | null = null;
function getWatermarkImage(): Promise<HTMLImageElement> {
  if (!watermarkImagePromise) {
    watermarkImagePromise = loadImage(WATERMARK_LOGO_URL);
  }
  return watermarkImagePromise;
}

function drawWatermark(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  logo: HTMLImageElement,
) {
  const targetWidth = Math.round(width * 0.3);
  const ratio = logo.naturalHeight / logo.naturalWidth;
  const targetHeight = Math.round(targetWidth * ratio);
  const x = Math.round((width - targetWidth) / 2);
  const y = Math.round((height - targetHeight) / 2);

  ctx.save();
  ctx.globalAlpha = 0.4;
  ctx.drawImage(logo, x, y, targetWidth, targetHeight);
  ctx.restore();
}

/**
 * Procesa una imagen:
 * 1. Escala si supera 1600px en cualquier dimensión.
 * 2. Comprime como WebP (calidad 0.72).
 * 3. Genera versión con marca de agua.
 *
 * Devuelve dos Blobs listos para subir a Supabase Storage.
 */
export async function processImage(file: File): Promise<ProcessedImage> {
  if (!file.type.startsWith("image/")) {
    throw new Error("El archivo no es una imagen");
  }

  const img = await loadImage(URL.createObjectURL(file));

  // Calcular dimensiones escaladas
  let { width, height } = img;
  if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
    const ratio = Math.min(MAX_DIMENSION / width, MAX_DIMENSION / height);
    width = Math.round(width * ratio);
    height = Math.round(height * ratio);
  }

  // Canvas principal
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("No se pudo obtener contexto 2D del canvas");

  // Imagen escalada (original comprimido)
  ctx.drawImage(img, 0, 0, width, height);
  const originalBlob = await canvasToBlob(canvas, OUTPUT_FORMAT, IMAGE_QUALITY);

  // Marca de agua (logo Bizaclia centrado, 30% ancho, 40% opacidad)
  const logo = await getWatermarkImage();
  drawWatermark(ctx, width, height, logo);
  const watermarkedBlob = await canvasToBlob(canvas, OUTPUT_FORMAT, IMAGE_QUALITY);

  URL.revokeObjectURL(img.src);

  return {
    original: originalBlob,
    watermarked: watermarkedBlob,
    width,
    height,
    sizeBytes: originalBlob.size,
    mimeType: "image/webp",
  };
}

/**
 * Determina si un archivo es de tipo vídeo.
 */
export function isVideo(file: File): boolean {
  return file.type.startsWith("video/");
}

/**
 * Devuelve la URL pública a usar para mostrar un archivo media.
 * Si existe `watermark_path`, lo usa; si no, cae al `storage_path` original.
 */
export function getMediaPublicUrl(
  media: { storage_path: string; watermark_path?: string | null },
  bucket = "media",
): string {
  const path = media.watermark_path ?? media.storage_path;
  // En un componente React podrías usar el supabase client,
  // pero como el bucket es público, basta con la URL directa.
  const { VITE_SUPABASE_URL } = import.meta.env;
  return `${VITE_SUPABASE_URL}/storage/v1/object/public/${bucket}/${path}`;
}

/**
 * Extrae extensión de un nombre de archivo.
 */
export function extractExtension(filename: string): string {
  const m = filename.match(/\.([a-zA-Z0-9]+)$/);
  return m ? m[1].toLowerCase() : "";
}

/**
 * Genera un nombre de archivo único para storage.
 */
export function makeStoragePath(
  anuncioId: string,
  mediaId: string,
  variant: "original" | "watermark",
  extension: string,
): string {
  const ext = extension.replace(/^\.+/, "");
  if (variant === "watermark") {
    return `${anuncioId}/${mediaId}-wm.${ext}`;
  }
  return `${anuncioId}/${mediaId}.${ext}`;
}

// ────────── Cloudflare Stream ──────────
/** Iframe oficial de reproducción. */
export function getStreamIframeUrl(uid: string): string {
  return `https://iframe.videodelivery.net/${uid}`;
}
/** Thumbnail JPG generado automáticamente. */
export function getStreamThumbnailUrl(uid: string, time = "1s"): string {
  return `https://videodelivery.net/${uid}/thumbnails/thumbnail.jpg?time=${encodeURIComponent(time)}`;
}
/** HLS para reproductores propios. */
export function getStreamHlsUrl(uid: string): string {
  return `https://videodelivery.net/${uid}/manifest/video.m3u8`;
}
